import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';

type StudentLayoutOptions = {
  noPadding?: boolean;
  lockDocumentScroll?: boolean;
  fillParent?: boolean;
  hideSidebar?: boolean;
};

function routeLayoutDefaults(pathname: string): StudentLayoutOptions {
  const rest = pathname.replace(/^\/student\/?/, '').replace(/\/$/, '');

  if (rest.startsWith('assignments')) {
    return { lockDocumentScroll: true };
  }
  if (rest.startsWith('reading-comprehension')) {
    return { noPadding: true };
  }
  if (rest === 'activity') {
    return { noPadding: true };
  }
  if (rest.startsWith('ibooks/') && rest !== 'ibooks') {
    return { noPadding: true, lockDocumentScroll: true, fillParent: true };
  }

  return {};
}

const StudentLayoutOptionsContext = createContext<(opts: StudentLayoutOptions) => void>(() => {});

/** Override shell options for the current student route (e.g. in-session full-bleed). */
export function useStudentSectionLayoutOptions(options: StudentLayoutOptions): void {
  const setOptions = useContext(StudentLayoutOptionsContext);

  useEffect(() => {
    setOptions(options);
    return () => setOptions({});
  }, [options.noPadding, options.lockDocumentScroll, options.fillParent, options.hideSidebar, setOptions]);
}

export function StudentSectionLayout(): React.ReactElement {
  const { pathname } = useLocation();
  const [override, setOverride] = useState<StudentLayoutOptions>({});

  useEffect(() => {
    setOverride((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [pathname]);

  const defaults = useMemo(() => routeLayoutDefaults(pathname), [pathname]);

  const noPadding = override.noPadding ?? defaults.noPadding ?? false;
  const lockDocumentScroll = override.lockDocumentScroll ?? defaults.lockDocumentScroll ?? false;
  const fillParent = override.fillParent ?? defaults.fillParent ?? false;
  const hideSidebar = override.hideSidebar ?? defaults.hideSidebar ?? false;

  const setOptions = useCallback((opts: StudentLayoutOptions) => {
    setOverride(opts);
  }, []);

  return (
    <StudentLayoutOptionsContext.Provider value={setOptions}>
      <Layout
        showSidebar={!hideSidebar}
        noPadding={noPadding}
        lockDocumentScroll={lockDocumentScroll}
        fillParent={fillParent}
      >
        <Outlet />
      </Layout>
    </StudentLayoutOptionsContext.Provider>
  );
}
